// src/context/CartContext.jsx
import { createContext, useContext, useState, useEffect, useCallback } from "react";

const CartContext = createContext(null);
const CART_STORAGE_KEY = "ld_gift_cart";

export function CartProvider({ children }) {
  const [cart, setCart] = useState([]);
  const [initialized, setInitialized] = useState(false);

  // Load cart from storage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      if (stored) setCart(JSON.parse(stored));
    } catch {
      setCart([]);
    }
    setInitialized(true);
  }, []);

  // Persist cart whenever it changes
  useEffect(() => {
    if (!initialized) return;
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch {
      /* ignore storage errors */
    }
  }, [cart, initialized]);

  const addToCart = useCallback((gift, quantity = 1) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.gift._id === gift._id);
      if (existing) {
        return prev.map((item) =>
          item.gift._id === gift._id ? { ...item, quantity: item.quantity + quantity } : item
        );
      }
      return [...prev, { gift, quantity }];
    });
  }, []);

  const removeFromCart = useCallback((giftId) => {
    setCart((prev) => prev.filter((item) => item.gift._id !== giftId));
  }, []);

  const updateQuantity = useCallback((giftId, quantity) => {
    setCart((prev) =>
      prev.map((item) =>
        item.gift._id === giftId ? { ...item, quantity: Math.max(1, quantity) } : item
      )
    );
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce((sum, item) => sum + item.gift.priceNgn * item.quantity, 0);

  return (
    <CartContext.Provider
      value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart, cartCount, cartTotal }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
}